import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  makeStyles,
  CircularProgress,
  Typography,
  Box,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  content: {
    minWidth: 400,
    maxWidth: 500,
    minHeight: 200,
  },
  stepTitle: {
    marginBottom: 12,
    fontWeight: 600,
    fontSize: 14,
  },
  searchField: {
    marginBottom: 16,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  recipientList: {
    maxHeight: 240,
    overflowY: "auto",
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 8,
  },
  listItem: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  empty: {
    padding: 24,
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  successBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    color: theme.palette.success.main,
  },
}));

const STEP_SELECT_RECIPIENTS = 0;
const STEP_SUCCESS = 1;

export default function ForwardMessagesDialog({
  open,
  onClose,
  initialSelectedIds = [],
  onForwardComplete,
}) {
  const classes = useStyles();
  const [step, setStep] = useState(STEP_SELECT_RECIPIENTS);
  const [selectedTicketIds, setSelectedTicketIds] = useState(new Set());
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [extraMessage, setExtraMessage] = useState("");

  useEffect(() => {
    if (open) {
      setStep(STEP_SELECT_RECIPIENTS);
      setSelectedTicketIds(new Set());
      setSearchQ("");
      setSearchResults([]);
      setExtraMessage("");
    }
  }, [open]);

  const toggleTicket = (id) => {
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const searchRecipients = useCallback(async () => {
    if (!searchQ.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/messages/forward/search", {
        params: { q: searchQ.trim() },
      });
      setSearchResults(data.tickets || []);
    } catch (err) {
      toastError(err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchQ]);

  useEffect(() => {
    const t = setTimeout(searchRecipients, 300);
    return () => clearTimeout(t);
  }, [searchQ, searchRecipients]);

  const handleForward = async () => {
    if (initialSelectedIds.length === 0 || selectedTicketIds.size === 0) return;
    setForwarding(true);
    try {
      await api.post("/messages/forward", {
        messageIds: initialSelectedIds,
        ticketIds: Array.from(selectedTicketIds),
        extraMessage: extraMessage && extraMessage.trim() ? extraMessage.trim() : undefined,
      });
      setStep(STEP_SUCCESS);
      if (onForwardComplete) onForwardComplete();
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      toastError(err);
    } finally {
      setForwarding(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {step === STEP_SELECT_RECIPIENTS && i18n.t("forward.titleSelectRecipients")}
        {step === STEP_SUCCESS && i18n.t("forward.titleSuccess")}
      </DialogTitle>
      <DialogContent className={classes.content}>
        {step === STEP_SELECT_RECIPIENTS && (
          <>
            <Typography className={classes.stepTitle}>
              {i18n.t("forward.searchHint")}
            </Typography>
            <TextField
              className={classes.searchField}
              fullWidth
              variant="outlined"
              placeholder={i18n.t("forward.searchPlaceholder")}
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            {searching ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <div className={classes.recipientList}>
                {searchResults.length === 0 ? (
                  <div className={classes.empty}>
                    {searchQ.trim()
                      ? i18n.t("forward.noResults")
                      : i18n.t("forward.typeToSearch")}
                  </div>
                ) : (
                  searchResults.map((t) => (
                    <ListItem
                      key={t.id}
                      button
                      onClick={() => toggleTicket(t.id)}
                      className={classes.listItem}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedTicketIds.has(t.id)}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={t.contact?.name || t.contact?.number || `#${t.id}`}
                        secondary={
                          t.contact?.isGroup
                            ? i18n.t("forward.group") + " • " + (t.contact?.number || "")
                            : t.contact?.number
                        }
                      />
                    </ListItem>
                  ))
                )}
              </div>
            )}
            <Box mt={2}>
              <TextField
                label={i18n.t("forward.optionalMessageLabel", { defaultValue: "Optional message" })}
                placeholder={i18n.t("forward.optionalMessagePlaceholder", {
                  defaultValue: "Type an additional message to send after the forwarded messages",
                })}
                multiline
                minRows={2}
                fullWidth
                variant="outlined"
                value={extraMessage}
                onChange={(e) => setExtraMessage(e.target.value)}
              />
            </Box>
          </>
        )}

        {step === STEP_SUCCESS && (
          <div className={classes.successBox}>
            <CheckCircleIcon style={{ fontSize: 48, marginRight: 8 }} />
            <Typography variant="h6">{i18n.t("forward.sentSuccess")}</Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {step === STEP_SELECT_RECIPIENTS && (
          <>
            <Button onClick={onClose} color="default">
              {i18n.t("forward.cancel")}
            </Button>
            <Button
              onClick={handleForward}
              color="primary"
              variant="contained"
              disabled={selectedTicketIds.size === 0 || forwarding}
            >
              {forwarding ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                i18n.t("forward.forwardTo", { count: selectedTicketIds.size })
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
